import { Component, forwardRef, Inject, Input, LOCALE_ID, OnChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AaDatePipe } from '@assetaccountant/core/pipes/aa-date.pipe';
import { RegisterData, RegisterParams } from '@assetaccountant/core/resolvers/register.resolver';
import { toUniversalDateString } from '@assetaccountant/core/utils/date-utils';
import { TypedActivatedRoute } from '@assetaccountant/core/utils/route-utils';
import { emptyValue } from '@assetaccountant/core/utils/typed-forms';
import moment, { Moment } from 'moment';

import { CalendarV2Api } from '@assetaccountant/api/services/calendar-v2.api';
import { FinancialYearStart } from '../../models/financial-year-start';

@Component({
  selector: 'app-fin-year-selector',
  templateUrl: './fin-year-selector.component.html',
  styleUrls: ['./fin-year-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FinYearSelectorComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class FinYearSelectorComponent implements OnChanges, ControlValueAccessor {
  @Input() startDate: string;
  @Input() financialYearStart?: FinancialYearStart;
  @Input() allowEmpty = false;
  @Input() usePreviousEndDate = false;
  @Input() includeStartDate = false;
  @Input() registerId?: number;
  @Input() regimeId?: number;
  @Input() flexible = false;
  @Input() useYearDate = true;

  isDisabled = false;

  items: Array<{ value: string; displayLabel: string }> = [];

  value: string | null;

  private _onChange: (val: string | null) => void;
  private _onTouched: () => void;

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private calendarApi: CalendarV2Api,
    @Inject(ActivatedRoute) private route: TypedActivatedRoute<RegisterParams, RegisterData>,
  ) {}

  ngOnChanges() {
    this.items = [];

    if (!this.startDate) {
      return;
    }

    const toItem = (val: string | Moment, shiftDay: number = 0) => {
      val = moment.utc(val);
      if (this.usePreviousEndDate) {
        val.add(-1, 'day');
      }
      if (shiftDay) {
        val.add(shiftDay, 'day');
      }
      const value = toUniversalDateString(val);
      if (!value) {
        throw new Error(`Expected date value, got: ${val}`);
      }
      const displayLabel = new AaDatePipe(this.locale, this.route).transform(value);
      return { value, displayLabel };
    };

    this.calendarApi
      .getYears({
        registerId: this.registerId || null,
        regimeId: this.regimeId || null,
        startDate: this.startDate,
        endDate: this.getEndDate(),
      })
      .subscribe(({ years }) => {
        if (
          this.includeStartDate ||
          (toUniversalDateString(this.startDate, { throwOnFailure: true }) ===
            toUniversalDateString(years[0].startDate, { throwOnFailure: true }) &&
            !this.usePreviousEndDate)
        ) {
          this.items.push(toItem(this.startDate));
        }

        const fyStarts = new Set(years.map(x => x.startDate));

        for (const fyStart of Array.from(fyStarts).sort().slice(1)) {
          this.items.push(toItem(fyStart));
        }

        if (this.value) {
          this.writeValue(this.value);
        }
      });
  }

  onChange(event: string | null) {
    this._onChange?.(event);
  }

  onTouched() {
    this._onTouched?.();
  }

  registerOnChange(fn: (val: string | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  thereIsNoItemWithValue = (value: string | null) =>
    this.items.length > 0 && this.items.findIndex(i => i.value === value) === -1;

  writeValue(obj: string | null): void {
    this.value = toUniversalDateString(obj) || emptyValue<string>();

    if (this.value && this.thereIsNoItemWithValue(this.value)) {
      const valueOf = moment.utc(this.value).valueOf();

      let previousValue = '';
      let previousDiff = Infinity;
      for (const item of this.items) {
        const currentDiff = Math.abs(moment.utc(item.value).valueOf() - valueOf);
        if (currentDiff >= previousDiff) {
          break;
        }
        previousValue = item.value;
        previousDiff = currentDiff;
      }

      this.value = previousValue;
      setTimeout(() => this._onChange?.(this.value));
    }
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }

  onUseYearDateChange(event: boolean) {
    // If switching back to year dates from a flexible date, select the closest available date
    if (event) {
      this.writeValue(this.value);
    }
  }

  private getEndDate(): string | null {
    if (!this.startDate) {
      return null;
    }

    const start = moment.utc(this.startDate);
    const now = moment.utc();

    // End date should be 2 years from now. But if start date is in the future, use 2 years from then.
    const end = (start.isAfter(now) ? start : now).add(2, 'year');

    return toUniversalDateString(end);
  }
}
